import { format, isToday, isYesterday } from 'date-fns';
import type { RawChatMessage } from '@/services/chats/types';

/**
 * WhatsApp-style "last seen X" formatter. Pass `now` so the caller can keep
 * a single ticking clock and re-render the indicator without forcing this
 * helper to compute time on its own.
 */
export function formatLastSeen(lastSeen: number, now: number): string {
  const elapsed = now - lastSeen;
  if (elapsed < 60_000) return 'last seen just now';
  if (elapsed < 60 * 60_000) {
    const minutes = Math.floor(elapsed / 60_000);
    return `last seen ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  const date = new Date(lastSeen);
  if (isToday(date)) return `last seen today at ${format(date, 'HH:mm')}`;
  if (isYesterday(date)) return `last seen yesterday at ${format(date, 'HH:mm')}`;
  return `last seen ${format(date, 'dd/MM/yyyy')}`;
}

/**
 * Returns true for messages that should render as a centered system pill
 * (group membership events: added / removed / joined / left).
 */
export function isGroupSystemMessage(message: RawChatMessage): boolean {
  return (message.media_type || '').toUpperCase() === 'GROUP';
}

/**
 * Strip the server's `@TAG#{...}#TAG@` mention markup down to plain text.
 *
 * The server stores mentions as inline JSON tokens, e.g.
 *   "Hey @TAG#{"id":6191,"name":"Dibyajyoti Panda"}#TAG@, check this"
 * Mobile renders them as styled chips. When the user copies / shares a
 * message we don't want the raw JSON in their clipboard — replace each
 * token with `@Name` so the output is readable.
 */
const MENTION_TAG_RE = /@TAG#(\{.*?\})#TAG@/g;
export function stripChatTags(text: string): string {
  if (!text) return text;
  return text.replace(MENTION_TAG_RE, (_full, json: string) => {
    try {
      const parsed = JSON.parse(json) as { name?: string };
      const name = (parsed.name || '').trim();
      return name ? `@${name}` : '';
    } catch {
      return '';
    }
  });
}

/**
 * Derive WhatsApp-style delivery status for an outgoing message.
 *
 * Uses the per-recipient `message_status` array. `read_time` / `received_time`
 * are stringified epochs from the server — "0" or "" means "not yet".
 *
 *  - All recipients have read_time > 0   → 'seen'
 *  - All recipients have received_time > 0 (but not all read) → 'delivered'
 *  - Otherwise → 'sent'
 *
 * For groups the "all recipients" rule matches WhatsApp (the blue ticks only
 * flip once everyone has seen it). Returns 'sent' for optimistic messages
 * whose message_status array is empty.
 */
export function deriveMessageStatus(
  message: RawChatMessage,
  currentUserId?: number,
): 'sent' | 'delivered' | 'seen' {
  const statuses = Array.isArray(message.message_status)
    ? message.message_status
    : [];
  const recipients = statuses.filter(
    (s) => s.user_id !== null && s.user_id !== currentUserId,
  );
  if (recipients.length === 0) return 'sent';

  const toNum = (v: string | null | undefined): number => {
    if (!v) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const allRead = recipients.every((s) => toNum(s.read_time) > 0);
  if (allRead) return 'seen';

  const allDelivered = recipients.every((s) => toNum(s.received_time) > 0);
  if (allDelivered) return 'delivered';

  return 'sent';
}

/**
 * Compose a WhatsApp-style narrative for a group membership event.
 *
 * The server stores these as `media_type: "GROUP"` with a generic verb in
 * `chat_text` ("added this group", "removed this group", "joined this group",
 * "left this group") and the target user in `group_info`. Mobile renders the
 * action as "Sunil added Sayantan Ghosh" / "Sunil added you" / "Sunil joined"
 * — this helper produces the same narrative.
 */
export function composeGroupEventText(
  message: RawChatMessage,
  currentUserId?: number,
): string {
  const actor = message.sender_name?.trim() || 'Someone';
  const chatText = (message.chat_text || '').toLowerCase();

  // Pull the action verb out of phrases like "added this group".
  let action: 'added' | 'removed' | 'joined' | 'left' | null = null;
  if (chatText.includes('add')) action = 'added';
  else if (chatText.includes('remov')) action = 'removed';
  else if (chatText.includes('join')) action = 'joined';
  else if (chatText.includes('left') || chatText.includes('leave')) action = 'left';

  const info =
    message.group_info && typeof message.group_info === 'object'
      ? (message.group_info as { user_id?: number; name?: string; type?: string })
      : undefined;

  // group_info.type wins if chat_text was ambiguous.
  if (!action && info?.type) {
    const t = info.type.toLowerCase();
    if (t.includes('add')) action = 'added';
    else if (t.includes('remov')) action = 'removed';
    else if (t.includes('join')) action = 'joined';
    else if (t.includes('left') || t.includes('leave')) action = 'left';
  }

  if (!action) {
    return `${actor} ${message.chat_text || ''}`.trim();
  }

  // Self-join / self-leave: no target needed.
  if (action === 'joined' || action === 'left') {
    return `${actor} ${action}`;
  }

  // Self-add: the actor and target are the same person. Treat it as a join
  // event so the pill reads "Sunil joined" instead of "Sunil added Sunil".
  const actorId = message.from_user_id ?? message.sender_id;
  if (
    info?.user_id !== undefined &&
    actorId !== undefined &&
    info.user_id === actorId
  ) {
    return `${actor} joined`;
  }

  const targetIsMe =
    info?.user_id !== undefined &&
    currentUserId !== undefined &&
    info.user_id === currentUserId;
  if (targetIsMe) return `${actor} ${action} you`;

  const targetName = info?.name?.trim();
  if (targetName) return `${actor} ${action} ${targetName}`;

  // Fall back to the actor + verb if the server didn't include a target.
  return `${actor} ${action}`;
}

/**
 * Formats a message preview text based on media type and chat text.
 * If chat_text is present, it returns the text directly.
 * Otherwise, it formats the media_type to a human-readable preview string.
 * This is required in threadList component to display the preview text in the chat list.
 */
export function formatMessagePreview(
  message: RawChatMessage,
  currentUserId?: number,
): string {
  // Tombstone — keep the sidebar preview in sync with the in-thread bubble
  // so a deleted message reads "You deleted this message" / "This message was
  // deleted" instead of leaking the original text via the preview.
  if (message.is_delete === 'Y') {
    const senderId = message.from_user_id ?? message.sender_id;
    const deletedBySelf =
      currentUserId !== undefined && senderId === currentUserId;
    return deletedBySelf
      ? '🚫 You deleted this message'
      : '🚫 This message was deleted';
  }

  const mediaType = message.media_type || 'TEXT';
  const chatText = message.chat_text || '';

  // Group membership events get a composed narrative so the sidebar reads
  // "Sunil added you" instead of the raw "added this group".
  if (isGroupSystemMessage(message)) {
    return composeGroupEventText(message, currentUserId);
  }

  // Call invites carry chat_text = "Voice Call" / "Video Call" as a plain
  // label, not user content. Short-circuit to the iconified preview before
  // the chat_text branch below claims them.
  const upperType = mediaType.toUpperCase();
  if (upperType === 'VOICE_CALL') return '📞 Voice call';
  if (upperType === 'VIDEO_CALL') return '📹 Video call';

  // If there's text, use it (for TEXT type or when text is present). The
  // sidebar can't render React chips, so collapse the mention markup to
  // `@Name` plain text first — the bubble in the open thread still renders
  // styled chips via the RichText component.
  if (chatText) {
    return stripChatTags(chatText);
  }

  // Format media type to sentence case and return appropriate preview
  switch (mediaType.toUpperCase()) {
    case 'TEXT':
      return 'No messages';
    case 'IMAGE':
      return '📷 Image';
    case 'VIDEO':
      return '🎥 Video';
    case 'AUDIO':
      return '🎵 Audio';
    case 'DOCUMENT':
      return '📄 Document';
    case 'LOCATION':
      return '📍 Location';
    case 'CONTACT':
      return '👤 Contact';
    case 'ANNOUNCEMENT':
      return '📢 Announcement';
    case 'ATTENDANCE':
      return '🚩 Attendance';
    case 'TASK':
      return '📋 Task';
    case 'MEET':
      return '📅 Meet';
    case 'VOICE_CALL':
      return '📞 Voice call';
    case 'VIDEO_CALL':
      return '📹 Video call';
    case 'DRIVE':
      return '📁 Shared Access';
    case 'NOTE':
      return '📝 Shared notes';
    case 'STATUS':
      return 'Status update';
    case 'POLL':
      return '📊 Poll';
    case 'POLL_REMINDER':
      return '🔔 Poll reminder';
    case 'EMPLOYEE_FENCE':
      return 'Employee fence';
    case 'PUNCH_IN_APPROVAL':
      return 'Punch in approval';
    case 'GROUP':
      return 'Group';
    case 'LEAVE_APPROVAL':
      return 'Leave approval';
    default:
      // Convert to sentence case for unknown types
      return mediaType
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
  }
}


/** One typing participant, resolved to a displayable name + avatar. */
export interface TypingParticipant {
  userId: number;
  name: string;
  image: string;
}

/**
 * Resolve the typing user ids for a thread into a list of named participants,
 * plus a short summary line ("Alice is typing…", "Alice, Bob are typing…",
 * "Alice and 3 others are typing…").
 *
 * Name lookup tries, in order:
 *   1. The contact-store entry whose `taged_user_id` matches.
 *   2. The most recent message in the thread sent by that user (sender_name).
 *
 * If neither yields a name we drop the participant from the summary — mobile
 * does the same; a bare "User is typing…" with no real name would be noise.
 * The result is also memo-friendly: stable arrays so React Compiler can cache.
 */
export function resolveTypingParticipants(
  typingUserIds: number[],
  recentMessages: RawChatMessage[],
  contacts: { taged_user_id: number; user_contact_name?: string; user_image?: string }[],
): { participants: TypingParticipant[]; summary: string } {
  if (typingUserIds.length === 0) {
    return { participants: [], summary: '' };
  }

  // Build a fast contact lookup once per call.
  const contactById = new Map<number, { name: string; image: string }>();
  for (const c of contacts) {
    if (!c.taged_user_id) continue;
    const name = (c.user_contact_name || '').trim();
    if (!name) continue;
    contactById.set(c.taged_user_id, { name, image: c.user_image || '' });
  }

  const participants: TypingParticipant[] = [];
  for (const id of typingUserIds) {
    const fromContact = contactById.get(id);
    if (fromContact) {
      participants.push({ userId: id, name: fromContact.name, image: fromContact.image });
      continue;
    }
    // Walk recent messages for a sender_name fallback — cheap because the hot
    // cache is at most ~50 messages per thread.
    const msg = recentMessages.find((m) => m.from_user_id === id);
    const fallbackName = msg?.sender_name?.trim();
    if (fallbackName) {
      participants.push({ userId: id, name: fallbackName, image: msg?.sender_image || '' });
    }
  }

  if (participants.length === 0) {
    // We knew people were typing but couldn't resolve any name — fall back to
    // the generic indicator so the user still sees *something*.
    return { participants: [], summary: 'typing…' };
  }

  // Use first names only — full names would overrun the header on long
  // multi-typer summaries. Matches WhatsApp.
  const firstName = (full: string) => full.split(/\s+/)[0] || full;

  if (participants.length === 1) {
    return {
      participants,
      summary: `${firstName(participants[0].name)} is typing…`,
    };
  }
  if (participants.length === 2) {
    return {
      participants,
      summary: `${firstName(participants[0].name)}, ${firstName(participants[1].name)} are typing…`,
    };
  }
  const remaining = participants.length - 1;
  return {
    participants,
    summary: `${firstName(participants[0].name)} and ${remaining} others are typing…`,
  };
}

interface ChatSocketEvent {
  event_type?: string;
  id?: number;
  action_type?: string;
  type?: string;
  data?: {
    group_id?: number | null;
    user_id?: number | null;
    from_user_id?: number | null;
    to_user_id?: number | null;
    [key: string]: unknown;
  };
  user_id?: number;
  from_user_id?: number;
  to_user_id?: number;
  [key: string]: unknown;
}

/**
 * This is a quick fix for getting the thread id from the socket event data to match the format
 * used in stored threads. chat-list-socket api returns room_id (threadId) as chat_6192_6191 format.
 * but socket event gives it as just 6192 format
 */
export function getThreadId(event: ChatSocketEvent){
  // For groups, use group_{group_id}
  if (event.event_type === 'group') {
    let groupId = event.id;

    if (event.action_type === 'chat'){
      groupId = event.data?.group_id ?? undefined;
    }
    return `group_${groupId}`;
  }else if(event.event_type === 'to_user'){
    // For single chats, get user IDs (can be from user_id/to_user_id or from_user_id/to_user_id)
    const userId1 = event.data?.user_id ?? event.data?.from_user_id ?? undefined;
    const userId2 = event.data?.to_user_id ?? undefined;

    // Sort user IDs in descending order to match API format
    if (userId1 && userId2 && userId1 > 0 && userId2 > 0) {
      const maxId = Math.max(userId1, userId2);
      const minId = Math.min(userId1, userId2);
      return `chat_${maxId}_${minId}`;
    }
  }
  else if(event.event_type === 'chat' && event.type === 'typing_status'){
    // For single chats, get user IDs (can be from user_id/to_user_id or from_user_id/to_user_id)
    const userId1 = event.user_id || event.from_user_id;
    const userId2 = event.to_user_id;

    // Sort user IDs in descending order to match API format
    if (userId1 && userId2 && userId1 > 0 && userId2 > 0) {
      const maxId = Math.max(userId1, userId2);
      const minId = Math.min(userId1, userId2);
      return `chat_${maxId}_${minId}`;
    }
  }
  
  console.warn('[getThreadId] Could not determine threadId:', event);
  return null;
}

/**
 * Chat thread structure with metadata and messages.
 * 
 * Groups messages by room_id (as per API terminology) and includes
 * metadata for display in the chat list.
 * 
 * @property meta - Thread metadata including room_id, chat_mode, unread_count, etc.
 * @property messages - Array of raw messages from the API, kept in original format
 */
export interface ChatThreadWithMessages {
  meta: {
    /** Room ID from API (e.g., 'chat_123' or 'group_456') */
    room_id: string;
    /** Chat mode: 'single' for one-on-one chats, 'group' for group chats */
    chat_mode: 'single' | 'group';
    /** Number of unread messages in this thread */
    unread_count: number;
    /** Formatted preview text for the last message (based on media_type) */
    preview_text: string;
    /** Display title: other user's name for single chats, group name for group chats */
    title: string;
    /** Avatar image URL: other user's image for single chats, group image for group chats */
    avatar: string;
    /** User ID: other user's ID for single chats, undefined for group chats */
    userId: number | undefined;
    /** Message status: 'sent', 'delivered', or 'seen' */
    status: 'sent' | 'delivered' | 'seen';
  };
  /** Array of raw chat messages from API, kept in original API format */
  messages: RawChatMessage[];
}

/**
 * Transforms raw chat messages from the API into organized chat threads.
 * 
 * This function:
 * - Groups messages by room_id (matching API terminology)
 * - Processes both single chats (room_id starts with 'chat_') and group chats (starts with 'group_')
 * - Calculates metadata for each thread (unread count, preview text, title)
 * - Sorts threads by most recent message time
 * 
 * **Performance**: Uses a single pass algorithm with O(n) time complexity.
 * Messages are expected to be in descending order (newest first) from the API.
 * 
 * @param rawMessages - Array of raw chat messages from the API
 * @param currentUserId - Current authenticated user's ID
 * @param groupMap - Optional map of group_id to { name, image } for group chat metadata
 *                  If not provided, defaults to empty Map
 * 
 * @remarks
 * - Filters out deleted messages (is_delete === 'Y' or is_delete_for_me === 'Y')
 * - Only processes messages with room_id starting with 'chat_' or 'group_'
 * - Unread count is calculated based on message_status.read_time for messages not from current user
 * - Thread title is extracted from the newest message in the thread
 */
export const transformMessagesToThreads = (
  rawMessages: RawChatMessage[],
  currentUserId: number,
  groupMap: Map<number, { name: string; image: string }> = new Map(),
): ChatThreadWithMessages[] => {
  // Early return for invalid inputs
  if (!currentUserId || currentUserId <= 0 || !rawMessages.length) {
    return [];
  }

  // Build threads array directly in single pass
  // Messages are already in descending order (newest first) from API, so process normally
  const threads: ChatThreadWithMessages[] = [];
  const roomIndexMap = new Map<string, number>(); // Map room_id to thread index in threads array

  // Single pass: group messages by room_id, build threads, and calculate metadata
  for (let i = 0; i < rawMessages.length; i++) {
    const msg = rawMessages[i];
    
    // Skip invalid + per-user-deleted messages. Globally-deleted messages
    // (`is_delete === 'Y'`) are KEPT so the bubble can render as a "this
    // message was deleted" tombstone — mirrors iOS / Android, which preserve
    // the timeline position. Per-user deletes stay hidden to the deleter.
    if (!msg.room_id || msg.is_delete_for_me === 'Y') {
      continue;
    }

    // Only process chat and group messages (room_id starts with 'chat_' or 'group_')
    if (!msg.room_id.startsWith('chat_') && !msg.room_id.startsWith('group_')) {
      continue;
    }

    const roomId = msg.room_id;
    let threadIndex = roomIndexMap.get(roomId);

    // Create new thread if it doesn't exist yet
    if (threadIndex === undefined) {
      // This is the first (newest) message for this thread
      const chatMode = msg.chat_mode === 'group' ? 'group' : 'single';
      const previewText = formatMessagePreview(msg, currentUserId);

      // Extract thread title, avatar, and userId from the newest message
      let title: string;
      let avatar: string = '';
      let userId: number | undefined = undefined;
      
      if (chatMode === 'single') {
        // For single chats: use the other user's name, image, and ID
        if (msg.from_user_id === currentUserId) {
          title = msg.to_user_name || 'Unknown User';
          avatar = msg.to_user_image || '';
          userId = msg.to_user_id ?? undefined;
        } else {
          title = msg.sender_name || 'Unknown User';
          avatar = msg.sender_image || '';
          userId = msg.from_user_id;
        }
      } else {
        // For group chats, prefer the user-group-list map (canonical name +
        // image), then fall back to the per-message `group_info` blob the
        // server attaches to group messages, and only show 'Group Chat' if
        // nothing else is available — never the raw `group_<id>` room id.
        const groupId = msg.group_id;
        const groupInfo = groupId ? groupMap.get(groupId) : undefined;
        const inlineGroupInfo =
          msg.group_info && typeof msg.group_info === 'object'
            ? (msg.group_info as { group_name?: string; group_image?: string })
            : undefined;
        title =
          groupInfo?.name ||
          inlineGroupInfo?.group_name ||
          'Group Chat';
        avatar = groupInfo?.image || inlineGroupInfo?.group_image || '';
        userId = undefined;
      }

      // Create new thread entry
      threadIndex = threads.length;
      threads.push({
        meta: {
          room_id: roomId,
          chat_mode: chatMode,
          unread_count: 0,
          preview_text: previewText,
          title,
          avatar,
          userId,
          status: 'sent', // Default status is 'sent'
        },
        messages: [],
      });
      roomIndexMap.set(roomId, threadIndex);
    }

    // Add message to the thread
    const thread = threads[threadIndex];
    thread.messages.push(msg);

    // Count unread messages (only for messages not sent by current user)
    if (msg.from_user_id !== currentUserId) {
      const status = msg.message_status?.find(s => s.user_id === currentUserId);
      // Message is unread if read_time is '0' or empty string
      if (status && (status.read_time === '0' || status.read_time === '')) {
        thread.meta.unread_count++;
      }
    }
  }

  // // Sort threads by newest message time (most recent first)
  // // Since messages are in descending order, index 0 is the newest message
  // threads.sort((a, b) => {
  //   const timeA = a.messages[0]?.created_time || 0;
  //   const timeB = b.messages[0]?.created_time || 0;
  //   return timeB - timeA; // Descending order (newest first)
  // });

  return threads;
};

/**
 * Computes unread message counts per meeting from raw chat messages.
 *
 * Scans messages whose room_id starts with "meet_", extracts the meeting ID,
 * and counts messages where the current user's message_status has read_time === "0".
 * Only messages NOT sent by the current user are counted.
 *
 * @returns Map of meeting_id (number) -> unread count
 */
export function computeMeetingUnreadCounts(
  rawMessages: RawChatMessage[],
  currentUserId: number,
): Map<number, number> {
  const counts = new Map<number, number>();

  if (!currentUserId || currentUserId <= 0 || !rawMessages.length) {
    return counts;
  }

  for (const msg of rawMessages) {
    if (!msg.room_id?.startsWith('meeting_') || msg.is_delete === 'Y' || msg.is_delete_for_me === 'Y') {
      continue;
    }

    // Skip the current user's own genuine messages, but DO count system events
    // (`admin_auto` add/reminder/status), which are attributed to the creator
    // yet still count toward the room's unread badge on iOS/Android.
    if (msg.from_user_id === currentUserId && Number(msg.admin_auto) !== 1) continue;

    const meetingIdStr = msg.room_id.slice(8); // "meeting_100020366" → "100020366"
    const meetingId = Number(meetingIdStr);
    if (!Number.isFinite(meetingId) || meetingId <= 0) continue;

    const status = msg.message_status?.find(s => s.user_id === currentUserId);
    if (status && (status.read_time === '0' || status.read_time === '')) {
      counts.set(meetingId, (counts.get(meetingId) ?? 0) + 1);
    }
  }

  return counts;
}

/**
 * Computes unread message counts per task from raw chat messages.
 *
 * Scans messages whose room_id starts with "task_", extracts the task ID,
 * and counts messages where the current user's message_status has read_time === "0".
 * Only messages NOT sent by the current user are counted (you don't get unread for your own actions).
 *
 * @returns Map of task_id (number) → unread count
 */
export function computeTaskUnreadCounts(
  rawMessages: RawChatMessage[],
  currentUserId: number,
): Map<number, number> {
  const counts = new Map<number, number>();

  if (!currentUserId || currentUserId <= 0 || !rawMessages.length) {
    return counts;
  }

  for (const msg of rawMessages) {
    if (!msg.room_id?.startsWith('task_') || msg.is_delete === 'Y' || msg.is_delete_for_me === 'Y') {
      continue;
    }

    // Skip the current user's own genuine messages, but DO count system events
    // (`admin_auto` add/reminder/status), which are attributed to the creator
    // yet still count toward the room's unread badge on iOS/Android.
    if (msg.from_user_id === currentUserId && Number(msg.admin_auto) !== 1) continue;

    const taskIdStr = msg.room_id.slice(5); // "task_300017087" → "300017087"
    const taskId = Number(taskIdStr);
    if (!Number.isFinite(taskId) || taskId <= 0) continue;

    const status = msg.message_status?.find(s => s.user_id === currentUserId);
    if (status && (status.read_time === '0' || status.read_time === '')) {
      counts.set(taskId, (counts.get(taskId) ?? 0) + 1);
    }
  }

  return counts;
}

// ─── Task/Meeting unread feed (socket-driven, HTTP-fallback) ─────────────────
//
// The unread badges on the Tasks/Meetings pages are derived from the
// `chat.list` query cache (see use-task-unread-counts / use-meeting-unread-
// counts). To let the live socket drive those counts — instead of re-fetching
// `chat-list-socket` on every page visit — that cache holds an ACCUMULATING
// feed of `task_`/`meeting_` chat records, fed by the HTTP hydrator (baseline +
// socket-down fallback) AND by `useChatUnreadSocketSync` (live).

/** True for the chat rooms whose unread counts back the task/meeting badges. */
export function isTaskOrMeetingRoom(roomId?: string): boolean {
  return !!roomId && (roomId.startsWith('task_') || roomId.startsWith('meeting_'));
}

/**
 * Resolve the `task_<id>` / `meeting_<id>` room id for a chat record.
 *
 * The HTTP `chat-list-socket` response denormalises `room_id` to that form, but
 * LIVE socket (`event_data`) messages don't carry the column — `room_id`
 * arrives empty (the schema defaults it to `''`). The foreign keys the payload
 * DOES carry (`task_id` / `meeting_id`) let us reconstruct the same room id, so
 * the live path and the HTTP path converge on one value and the unread-count
 * helpers (which slice `room_id`) see the right room either way.
 *
 * Returns null when the record belongs to neither a task nor a meeting room.
 */
export function resolveTaskMeetRoomId(msg: {
  room_id?: string;
  task_id?: number | null;
  meeting_id?: number | null;
}): string | null {
  if (isTaskOrMeetingRoom(msg.room_id)) return msg.room_id as string;
  const taskId = Number(msg.task_id ?? 0);
  if (Number.isFinite(taskId) && taskId > 0) return `task_${taskId}`;
  const meetingId = Number(msg.meeting_id ?? 0);
  if (Number.isFinite(meetingId) && meetingId > 0) return `meeting_${meetingId}`;
  return null;
}

/**
 * Merge a batch of incoming chat records (from an HTTP sync OR a socket event)
 * into the accumulating task/meeting unread feed: keep only task_/meeting_
 * rows, dedupe by message id (newest wins — it carries the freshest
 * `message_status`), and drop `is_delete` rows. Returns the previous array
 * unchanged when nothing relevant changed, so `select` observers don't
 * re-render needlessly.
 */
export function mergeTaskMeetUnreadFeed(
  prev: RawChatMessage[],
  incoming: RawChatMessage[],
): RawChatMessage[] {
  if (incoming.length === 0) return prev;

  const byId = new Map<number, RawChatMessage>();
  for (const m of prev) {
    const id = Number(m.id);
    if (id > 0) byId.set(id, m);
  }

  let changed = false;
  for (const m of incoming) {
    if (!isTaskOrMeetingRoom(m.room_id)) continue;
    const id = Number(m.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    if (m.is_delete === 'Y' || m.is_delete_for_me === 'Y') {
      if (byId.delete(id)) changed = true;
      continue;
    }
    byId.set(id, m);
    changed = true;
  }

  return changed ? Array.from(byId.values()) : prev;
}

/**
 * Build an "unread by the current user" status row for a freshly-arrived
 * task/meeting message that didn't include the current user's status — so the
 * badge increments (computeTask/MeetingUnreadCounts only counts a message when
 * the user has a status row with an empty `read_time`).
 */
export function makeUnreadStatusRow(
  currentUserId: number,
  chatId: number,
): RawChatMessage['message_status'][number] {
  return {
    id: 0,
    user_id: currentUserId,
    read_time: '',
    received_time: '',
    reaction: null,
    reaction_time: null,
    call_status: null,
    call_time: null,
    chat_id: chatId,
  };
}

export type ChatReceiptUpdate = {
  chatId: number;
  userId: number;
  received_time?: string | number;
  read_time?: string | number;
};

/**
 * Normalise the receipt payload shapes the chat socket emits
 * (`chat_received_*`, `chat_read_*`, `*_status_all`) into a flat list of
 * (chatId, userId, received_time?, read_time?) updates. Mirrors the inline
 * parser in use-chat-hydrate's store-side receipt handler — kept separate so
 * the unread-feed sync doesn't destabilise that handler.
 */
export function extractChatReceiptUpdates(
  raw: unknown,
  kind: 'received' | 'read' | 'either',
): ChatReceiptUpdate[] {
  const parseMaybeJson = (value: unknown): unknown => {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };
  const num = (v: unknown): number | undefined => {
    if (v === null || v === undefined) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const updates: ChatReceiptUpdate[] = [];
  const push = (
    chatIdRaw: unknown,
    userIdRaw: unknown,
    receivedRaw?: unknown,
    readRaw?: unknown,
  ): void => {
    const chatId = num(chatIdRaw);
    const userId = num(userIdRaw);
    if (!chatId || chatId <= 0 || !userId || userId <= 0) return;
    const upd: ChatReceiptUpdate = { chatId, userId };
    const now = Date.now();
    const recv = num(receivedRaw);
    const read = num(readRaw);
    if (kind === 'received' || recv !== undefined) upd.received_time = recv ?? now;
    if (kind === 'read' || read !== undefined) upd.read_time = read ?? now;
    updates.push(upd);
  };

  const walk = (node: unknown): void => {
    if (!node) return;
    if (Array.isArray(node)) {
      for (const item of node) walk(item);
      return;
    }
    if (typeof node !== 'object') return;
    const obj = node as Record<string, unknown>;
    if (Array.isArray(obj.receipt)) {
      const parentChatId = obj.chat_id !== undefined ? obj.chat_id : obj.id;
      for (const r of obj.receipt) {
        if (!r || typeof r !== 'object') continue;
        const row = r as Record<string, unknown>;
        push(row.chat_id ?? row.id ?? parentChatId, row.user_id, row.received_time, row.read_time);
      }
      return;
    }
    push(obj.chat_id ?? obj.id ?? obj.message_id, obj.user_id, obj.received_time, obj.read_time);
  };

  walk(parseMaybeJson(raw));
  return updates;
}

/**
 * Apply read/received receipts to the unread feed. Only the CURRENT user's
 * status governs their badge, so we update (or insert) that one row. Returns a
 * new array only when something actually changed.
 */
export function applyReceiptsToUnreadFeed(
  prev: RawChatMessage[],
  updates: ChatReceiptUpdate[],
  currentUserId: number,
): RawChatMessage[] {
  if (prev.length === 0 || updates.length === 0 || !currentUserId) return prev;

  const byChatId = new Map<number, ChatReceiptUpdate>();
  for (const u of updates) {
    if (u.userId !== currentUserId) continue;
    byChatId.set(u.chatId, u);
  }
  if (byChatId.size === 0) return prev;

  let changed = false;
  const next = prev.map((m) => {
    const upd = byChatId.get(Number(m.id));
    if (!upd) return m;

    const read = upd.read_time != null ? String(upd.read_time) : undefined;
    const received = upd.received_time != null ? String(upd.received_time) : undefined;
    const statuses = m.message_status ?? [];

    let rowChanged = false;
    let sawMine = false;
    const nextStatuses = statuses.map((s) => {
      if (s.user_id !== currentUserId) return s;
      sawMine = true;
      const ns = { ...s };
      if (read && read !== '0' && ns.read_time !== read) {
        ns.read_time = read;
        rowChanged = true;
      }
      if (received && received !== '0' && ns.received_time !== received) {
        ns.received_time = received;
        rowChanged = true;
      }
      return rowChanged ? ns : s;
    });

    if (!sawMine && (read || received)) {
      nextStatuses.push({
        ...makeUnreadStatusRow(currentUserId, Number(m.id)),
        read_time: read && read !== '0' ? read : '',
        received_time: received && received !== '0' ? received : '',
      });
      rowChanged = true;
    }

    if (!rowChanged) return m;
    changed = true;
    return { ...m, message_status: nextStatuses };
  });

  return changed ? next : prev;
}
